import { endOfDay, isValid, parseISO, startOfDay } from "date-fns";

import { ISearchOrderRedirectFilters } from "@/main/hooks/useStripeCheckoutRedirect";
import { KeyValue } from "@/main/models/common";
import { QuoteResponse } from "@/main/models/response";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";

import { TypeDataFormCreateOrderBodyshop } from "./context";
import { VehicleModeEnum } from "./steps/vehicleData/types";

export function getQuoteBlockedReasonKey(result: QuoteResponse): string {
	if (result.idProcurementStatus === ProcurementStatusEnum.Cancelled) return "information.quoteCanceled";
	if (result.quoteBlocked) return "information.quoteBlocked";
	if (result.paymentPending) return "information.paymentPending";
	return "";
}

export interface IQuoteFlags {
	bodyShop: string;
	claimNumber: string;
	code: number | null;
	guidBodyShop: string;
	guidCompanyBuyer: string;
	idProcurementStatus: number | null;
	integration: boolean;
	isPaymentPending: boolean;
	quoteDate: string;
}

export const INITIAL_QUOTE_FLAGS: IQuoteFlags = {
	bodyShop: "",
	claimNumber: "",
	code: null,
	guidBodyShop: "",
	guidCompanyBuyer: "",
	idProcurementStatus: null,
	integration: false,
	isPaymentPending: false,
	quoteDate: "",
};

export function buildQuoteFlagsFromQuote(result: QuoteResponse): IQuoteFlags {
	return {
		bodyShop: result.bodyShop ?? "",
		claimNumber: result.claimNumber ?? "",
		code: result.code ?? null,
		guidBodyShop: result.guidBodyShop ?? "",
		guidCompanyBuyer: result.guidCompanyBuyer ?? "",
		idProcurementStatus: result.idProcurementStatus ?? null,
		integration: Boolean(result.integration),
		isPaymentPending: Boolean(result.paymentPending),
		quoteDate: result.startDate ?? "",
	};
}

export function buildSearchOrderFiltersFromQuote({
	claimNumber,
	code,
	quoteDate,
}: IQuoteFlags): ISearchOrderRedirectFilters {
	if (claimNumber) return { quoteFilterVehicle: claimNumber };

	const quoteFilterVehicle = code === null ? "" : String(code);
	const parsedQuoteDate = parseISO(quoteDate);

	if (!isValid(parsedQuoteDate)) return { quoteFilterVehicle };

	return {
		quoteFilterVehicle,
		startDate: startOfDay(parsedQuoteDate).toISOString(),
		finalDate: endOfDay(parsedQuoteDate).toISOString(),
	};
}

function buildKeyValue(key: string | null, value: string | null): KeyValue | null {
	if (!key) return null;
	return { key, value: value ?? "" };
}

export function buildVehicleDataFromQuote(result: QuoteResponse): TypeDataFormCreateOrderBodyshop["vehicleData"] {
	return {
		mode: VehicleModeEnum.Manual,
		photos: {},
		isDrivable: true,
		aiResult: null,
		guidCountry: result.vehicleGuidCountry ?? null,
		plate: result.vehicleLicensePlate ?? "",
		vin: result.vehicleVIN ?? "",
		manualForm: {
			country: null,
			year: buildKeyValue(result.vehicleGuidYear, result.vehicleYear),
			manufacturer: buildKeyValue(result.vehicleGuidManufacturer, result.vehicleManufacturer),
			model: buildKeyValue(result.vehicleGuidVehicleModel, result.vehicleModel),
			trim: buildKeyValue(result.vehicleGuidSubModel, result.vehicleSubModel),
			plate: result.vehicleLicensePlate ?? "",
			vin: result.vehicleVIN ?? "",
		},
		vehicle: {
			guidVehicleYear: result.vehicleGuidYear ?? null,
			guidVehicleManufacturer: result.vehicleGuidManufacturer ?? null,
			guidVehicleModel: result.vehicleGuidVehicleModel ?? null,
			guidVehicleSubModel: result.vehicleGuidSubModel ?? null,
		},
	};
}

export function buildVehicleLabel(vehicleData: TypeDataFormCreateOrderBodyshop["vehicleData"]): string {
	const { manualForm, plate } = vehicleData;
	const vehicleName = [manualForm?.manufacturer, manualForm?.model, manualForm?.trim, manualForm?.year]
		.map((option) => option?.value ?? "")
		.filter(Boolean)
		.join(" ");
	const licensePlate = plate || manualForm?.plate || "";

	if (!vehicleName) return licensePlate;

	return licensePlate ? `${vehicleName} (${licensePlate})` : vehicleName;
}
